/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Event Dashboard Page - SuperAdmin Portal
 * ============================================================================
 *
 * Monitor and control domain event listeners.
 * View metrics, enable/disable listeners, and track event processing.
 *
 * ============================================================================
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  Zap,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Clock,
  TrendingUp,
  BarChart3,
  Radio,
  Settings2,
} from 'lucide-react';
import {
  useEventDashboardSummary,
  useToggleListener,
  useClearEventCache,
} from '@/hooks/api/useEventDashboard';
import { EventListenerInfo } from '@/services/superadmin/events.service';
import { useNotification } from '@/hooks/useNotification';
import { formatErrorForDisplay, isApiError } from '@/services/api/errors';

// Stat Card Component
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string | number;
  label: string;
  color: string;
}> = ({ icon, value, label, color }) => (
  <div className="stat-card">
    <div className={`stat-icon ${color}`}>{icon}</div>
    <div className="stat-value">{typeof value === 'number' ? value.toLocaleString() : value}</div>
    <div className="stat-label">{label}</div>
  </div>
);

// Status Badge Component
const StatusBadge: React.FC<{ enabled: boolean; t: (key: string) => string }> = ({
  enabled,
  t,
}) => {
  if (enabled) {
    return (
      <span className="status-badge status-active">
        <CheckCircle className="w-3 h-3" />
        {t('events.status.enabled')}
      </span>
    );
  }
  return (
    <span className="status-badge status-inactive">
      <XCircle className="w-3 h-3" />
      {t('events.status.disabled')}
    </span>
  );
};

// Module Badge Component
const ModuleBadge: React.FC<{ moduleCode: string }> = ({ moduleCode }) => {
  const getModuleColor = (code: string) => {
    const colors: Record<string, string> = {
      BILLING: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      PARTNER: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      AUTH: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      CORE: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };
    return colors[code?.toUpperCase()] || colors.CORE;
  };

  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getModuleColor(moduleCode)}`}>
      {moduleCode || 'CORE'}
    </span>
  );
};

// Event Row Component
const EventRow: React.FC<{
  listener: EventListenerInfo;
  onToggle: (eventType: string, listenerClass: string) => void;
  isToggling: boolean;
  t: (key: string) => string;
}> = ({ listener, onToggle, isToggling, t }) => {
  return (
    <tr className="table-row hover:bg-gray-50 dark:hover:bg-gray-800/50">
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <span className="font-medium text-gray-900 dark:text-white text-sm">
            {listener.eventType}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {listener.shortClassName}
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <ModuleBadge moduleCode={listener.moduleCode} />
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {listener.description || '-'}
        </span>
      </td>
      <td className="px-4 py-3">
        <StatusBadge enabled={listener.enabled} t={t} />
      </td>
      <td className="px-4 py-3">
        <button
          onClick={() => onToggle(listener.eventType, listener.listenerClass)}
          disabled={isToggling}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            listener.enabled
              ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50'
              : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50'
          } disabled:opacity-50`}
        >
          {isToggling ? (
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          ) : listener.enabled ? (
            <XCircle className="w-3.5 h-3.5" />
          ) : (
            <CheckCircle className="w-3.5 h-3.5" />
          )}
          {listener.enabled ? t('events.actions.disable') : t('events.actions.enable')}
        </button>
      </td>
    </tr>
  );
};

// Main Component
export const EventDashboardPage: React.FC = () => {
  const { t } = useTranslation('platform');
  const { success: showSuccess, error: showError } = useNotification();
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [togglingEvent, setTogglingEvent] = useState<string | null>(null);

  // Queries
  const {
    data: summary,
    isLoading,
    error,
    refetch,
  } = useEventDashboardSummary();

  // Mutations
  const toggleMutation = useToggleListener();
  const clearCacheMutation = useClearEventCache();

  // Handlers
  const handleToggle = async (eventType: string, listenerClass: string) => {
    setTogglingEvent(eventType);
    try {
      const result = await toggleMutation.mutateAsync({
        eventType,
        listenerClass,
        persist: true,
      });
      showSuccess(
        t(result.enabled ? 'events.messages.enabled' : 'events.messages.disabled')
      );
    } catch (err) {
      if (isApiError(err)) {
        const { title, message } = formatErrorForDisplay(err);
        showError(message, title);
      } else {
        showError(t('events.messages.toggleFailed'));
      }
    } finally {
      setTogglingEvent(null);
    }
  };

  const handleClearCache = async () => {
    try {
      await clearCacheMutation.mutateAsync();
      showSuccess(t('events.messages.cacheCleared'));
    } catch (err) {
      if (isApiError(err)) {
        const { title, message } = formatErrorForDisplay(err);
        showError(message, title);
      } else {
        showError(t('events.messages.clearCacheFailed'));
      }
    }
  };

  // Filter listeners by module
  const filteredListeners = summary?.listeners?.filter(
    (l) => moduleFilter === 'all' || l.moduleCode === moduleFilter
  ) || [];

  // Get unique modules
  const modules = [...new Set(summary?.listeners?.map((l) => l.moduleCode).filter(Boolean) || [])];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertTriangle className="w-12 h-12 text-red-500" />
        <p className="text-gray-600 dark:text-gray-400">{t('events.messages.loadFailed')}</p>
        <button
          onClick={() => refetch()}
          className="btn btn-primary"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Radio className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="page-title">{t('events.title')}</h1>
            <p className="page-subtitle">{t('events.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleClearCache}
            disabled={clearCacheMutation.isPending}
            className="btn btn-secondary"
          >
            {clearCacheMutation.isPending ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Settings2 className="w-4 h-4" />
            )}
            {t('events.actions.clearCache')}
          </button>
          <button
            onClick={() => refetch()}
            className="btn btn-secondary"
          >
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard
          icon={<Activity className="w-5 h-5" />}
          value={summary?.totalReceived || 0}
          label={t('events.stats.totalReceived')}
          color="stat-icon-blue"
        />
        <StatCard
          icon={<Zap className="w-5 h-5" />}
          value={summary?.queueDepth || 0}
          label={t('events.stats.queued')}
          color="stat-icon-yellow"
        />
        <StatCard
          icon={<Clock className="w-5 h-5" />}
          value={summary?.processingCount || 0}
          label={t('events.stats.processing')}
          color="stat-icon-purple"
        />
        <StatCard
          icon={<CheckCircle className="w-5 h-5" />}
          value={summary?.totalProcessed || 0}
          label={t('events.stats.success')}
          color="stat-icon-green"
        />
        <StatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          value={summary?.totalFailed || 0}
          label={t('events.stats.failed')}
          color="stat-icon-red"
        />
      </div>

      {/* Listeners Section */}
      <div className="card mt-6">
        <div className="card-header flex justify-between items-center">
          <div>
            <h2 className="card-title">{t('events.listeners.title')}</h2>
            <p className="card-subtitle">
              {summary?.enabledListeners || 0} {t('events.listeners.enabled')} / {' '}
              {summary?.disabledListeners || 0} {t('events.listeners.disabled')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              className="input input-sm"
            >
              <option value="all">{t('events.filters.allModules')}</option>
              {modules.map((mod) => (
                <option key={mod} value={mod}>
                  {mod}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left">{t('events.table.eventType')}</th>
                <th className="px-4 py-3 text-left">{t('events.table.module')}</th>
                <th className="px-4 py-3 text-left">{t('events.table.description')}</th>
                <th className="px-4 py-3 text-left">{t('events.table.status')}</th>
                <th className="px-4 py-3 text-left">{t('events.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredListeners.map((listener) => (
                <EventRow
                  key={`${listener.eventType}-${listener.listenerClass}`}
                  listener={listener}
                  onToggle={handleToggle}
                  isToggling={togglingEvent === listener.eventType}
                  t={t}
                />
              ))}
              {filteredListeners.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('events.messages.noListeners')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Last Updated */}
      {summary?.lastUpdatedAt && (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
          {t('events.lastUpdated')}: {new Date(summary.lastUpdatedAt).toLocaleString()}
        </div>
      )}

      <style>{`
        .page-container {
          padding: 1.5rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .page-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .page-subtitle {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
        }

        .stat-card {
          background: var(--color-bg-card);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          padding: 1.25rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .stat-icon {
          padding: 0.75rem;
          border-radius: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .stat-icon-blue { background: rgb(59 130 246 / 0.1); color: rgb(59 130 246); }
        .stat-icon-green { background: rgb(34 197 94 / 0.1); color: rgb(34 197 94); }
        .stat-icon-yellow { background: rgb(234 179 8 / 0.1); color: rgb(234 179 8); }
        .stat-icon-red { background: rgb(239 68 68 / 0.1); color: rgb(239 68 68); }
        .stat-icon-purple { background: rgb(168 85 247 / 0.1); color: rgb(168 85 247); }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text-primary);
        }

        .stat-label {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
          margin-top: 0.25rem;
        }

        .card {
          background: var(--color-bg-card);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          overflow: hidden;
        }

        .card-header {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--color-border);
        }

        .card-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .card-subtitle {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
          margin-top: 0.25rem;
        }

        .table {
          width: 100%;
        }

        .table th {
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--color-text-secondary);
          background: var(--color-bg-secondary);
        }

        .table-row {
          border-bottom: 1px solid var(--color-border);
        }

        .table-row:last-child {
          border-bottom: none;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.25rem 0.625rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .status-active {
          background: rgb(34 197 94 / 0.1);
          color: rgb(34 197 94);
        }

        .status-inactive {
          background: rgb(107 114 128 / 0.1);
          color: rgb(107 114 128);
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .btn-primary {
          background: var(--color-primary);
          color: white;
        }

        .btn-primary:hover {
          opacity: 0.9;
        }

        .btn-secondary {
          background: var(--color-bg-secondary);
          color: var(--color-text-primary);
          border: 1px solid var(--color-border);
        }

        .btn-secondary:hover {
          background: var(--color-bg-hover);
        }

        .input {
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: 0.375rem;
          background: var(--color-bg-input);
          color: var(--color-text-primary);
          font-size: 0.875rem;
        }

        .input-sm {
          padding: 0.375rem 0.625rem;
          font-size: 0.8125rem;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
};

export default EventDashboardPage;
